import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./AccessibilityPanel.css";
import { useModalA11y } from "./useModalA11y";
import { useI18n } from "../i18n";
import {
  AccessibilityIcon,
  ZoomInIcon,
  ZoomOutIcon,
  MoonIcon,
  LinkIcon,
  ResetIcon,
} from "../icons/Icons";
import { FONT_SCALE_MAX, FONT_SCALE_MIN } from "./useSettings";

type Props = {
  dark: boolean;
  setDark: (v: boolean) => void;
  fontScale: number;
  increaseFont: () => void;
  decreaseFont: () => void;
  underlineLinks: boolean;
  setUnderlineLinks: (v: boolean) => void;
  reset: () => void;
};

/**
 * Accessibility button + panel (replaces the header settings gear). Native —
 * no third-party overlay — so it works with assistive tech and keeps
 * everything in-app (NHS data residency). Presented as a simple list of tools.
 *
 * Two triggers, one state. On desktop it is the side-docked orange tab. On
 * mobile that tab covered the reading column and could not be moved (Priya,
 * PROJ-119), so the trigger becomes a normal pill in the header's action row,
 * beside "Get Urgent Help" — portalled into a slot the header renders, so it
 * is a real sibling of those buttons rather than something floating over them.
 * CSS decides which trigger is visible; only one is ever interactive.
 */
export function AccessibilityPanel(props: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const slot =
    typeof document !== "undefined"
      ? document.getElementById("a11y-header-slot")
      : null;
  // The mobile panel hangs below the header; its height varies with the
  // wrapped action rows, so measure rather than hard-code.
  const openPanel = () =>
    setOpen((o) => {
      const next = !o;
      if (next && typeof document !== "undefined") {
        const header = document.querySelector<HTMLElement>(".app-header");
        if (header) {
          document.documentElement.style.setProperty(
            "--a11y-panel-top",
            `${Math.round(header.getBoundingClientRect().bottom) + 8}px`,
          );
        }
      }
      return next;
    });
  const trigger = (className: string, iconSize: number) => (
    <button
      type="button"
      className={className}
      aria-label={t("a11y.title")}
      aria-expanded={open}
      aria-controls="a11y-panel"
      onClick={openPanel}
    >
      <AccessibilityIcon size={iconSize} />
    </button>
  );
  return (
    <>
      {trigger("a11y-tab", 32)}
      {slot && createPortal(trigger("a11y-header-btn", 20), slot)}
      {open && <Panel {...props} onClose={() => setOpen(false)} />}
    </>
  );
}

function Panel({
  dark,
  setDark,
  fontScale,
  increaseFont,
  decreaseFont,
  underlineLinks,
  setUnderlineLinks,
  reset,
  onClose,
}: Props & { onClose: () => void }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  useModalA11y(ref, onClose);

  const pct = Math.round(fontScale * 100);

  return (
    <div
      className="a11y-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        id="a11y-panel"
        className="a11y-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("a11y.title")}
      >
        <h2 className="a11y-panel__title">Accessibility Tools</h2>
        <ul className="a11y-list">
          <li>
            <button
              type="button"
              className="a11y-item"
              onClick={increaseFont}
              disabled={fontScale >= FONT_SCALE_MAX}
            >
              <ZoomInIcon /> <span>{t("a11y.increaseText")}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className="a11y-item"
              onClick={decreaseFont}
              disabled={fontScale <= FONT_SCALE_MIN}
            >
              <ZoomOutIcon /> <span>{t("a11y.decreaseText")}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className="a11y-item"
              aria-pressed={dark}
              onClick={() => setDark(!dark)}
            >
              <MoonIcon /> <span>{t("a11y.darkMode")}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className="a11y-item"
              aria-pressed={underlineLinks}
              onClick={() => setUnderlineLinks(!underlineLinks)}
            >
              <LinkIcon /> <span>{t("a11y.underlineLinks")}</span>
            </button>
          </li>
          <li>
            <button type="button" className="a11y-item" onClick={reset}>
              <ResetIcon size={18} /> <span>{t("a11y.reset")}</span>
            </button>
          </li>
        </ul>
        {/* Not shown visually; announces the new size to screen readers. */}
        <p className="visually-hidden" aria-live="polite">
          Text size {pct}%
        </p>
      </div>
    </div>
  );
}
